import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Users, Clock, CheckCircle2, XCircle, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const STATUS_COLORS = {
  approved: 'bg-success/20 text-success border-success/30',
  pending: 'bg-warning/20 text-warning border-warning/30',
  rejected: 'bg-error/20 text-error border-error/30',
};

export default function LeaveCalendarPage() {
  const [leaves, setLeaves] = useState([]);
  const { t } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [view, setView] = useState('calendar');

  const fetchData = async () => {
    try {
      const [leavesRes, empRes] = await Promise.all([
        api.get('/leaves'),
        api.get('/employees'),
      ]);
      setLeaves(leavesRes.data);
      setEmployees(empRes.data);
    } catch (err) {
      toast.error('Failed to load leave data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const getDaysInMonth = (month, year) => new Date(year, month + 1, 0).getDate();
  const getFirstDayOfMonth = (month, year) => new Date(year, month, 1).getDay();

  const daysInMonth = getDaysInMonth(currentMonth, currentYear);
  const firstDay = getFirstDayOfMonth(currentMonth, currentYear);

  const prevMonth = () => {
    if (currentMonth === 0) { setCurrentMonth(11); setCurrentYear(currentYear - 1); }
    else setCurrentMonth(currentMonth - 1);
  };
  const nextMonth = () => {
    if (currentMonth === 11) { setCurrentMonth(0); setCurrentYear(currentYear + 1); }
    else setCurrentMonth(currentMonth + 1);
  };

  const getLeavesForDay = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return leaves.filter(l => {
      const start = (l.start_date || '').slice(0, 10);
      const end = (l.end_date || '').slice(0, 10);
      return dateStr >= start && dateStr <= end;
    });
  };

  // Aggregate balances
  const employeeBalances = employees.filter(e => e.status === 'active').map(emp => {
    const empLeaves = leaves.filter(l => l.employee_id === emp.id && l.status === 'approved');
    const annualUsed = empLeaves.filter(l => l.leave_type === 'annual').reduce((s, l) => s + (l.days || 0), 0);
    const sickUsed = empLeaves.filter(l => l.leave_type === 'sick').reduce((s, l) => s + (l.days || 0), 0);
    return {
      ...emp,
      annual_used: annualUsed,
      annual_remaining: (emp.annual_leave_entitled || 30) - annualUsed,
      sick_used: sickUsed,
      sick_remaining: (emp.sick_leave_entitled || 15) - sickUsed,
    };
  });

  const pendingCount = leaves.filter(l => l.status === 'pending').length;
  const approvedCount = leaves.filter(l => l.status === 'approved').length;
  const onLeaveToday = getLeavesForDay(new Date().getDate()).filter(l => l.status === 'approved').length;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="leave-calendar-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
              <Calendar size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">Leave Calendar</h1>
              <p className="text-[13px] text-muted-foreground mt-0.5 font-medium">Calendar view with balance tracking</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant={view === 'calendar' ? 'default' : 'outline'} size="sm" onClick={() => setView('calendar')} data-testid="view-calendar-btn" className="rounded-xl h-9 text-[13px]">
              <Calendar size={14} className="mr-1.5" /> Calendar
            </Button>
            <Button variant={view === 'balances' ? 'default' : 'outline'} size="sm" onClick={() => setView('balances')} data-testid="view-balances-btn" className="rounded-xl h-9 text-[13px]">
              <Users size={14} className="mr-1.5" /> Balances
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="stat-card border-0 bg-white" data-testid="stat-on-leave">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">On Leave Today</p>
              <p className="text-2xl font-bold font-outfit text-info mt-1">{onLeaveToday}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white" data-testid="stat-pending">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-warning uppercase tracking-[0.08em]">Pending Requests</p>
              <p className="text-2xl font-bold font-outfit text-warning mt-1">{pendingCount}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white" data-testid="stat-approved">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-success uppercase tracking-[0.08em]">Approved This Year</p>
              <p className="text-2xl font-bold font-outfit text-success mt-1">{approvedCount}</p>
            </CardContent>
          </Card>
          <Card className="stat-card border-0 bg-white" data-testid="stat-employees">
            <CardContent className="pt-4 pb-3.5 px-4">
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-[0.08em]">Active Employees</p>
              <p className="text-2xl font-bold font-outfit text-purple-600 mt-1">{employeeBalances.length}</p>
            </CardContent>
          </Card>
        </div>

        {view === 'calendar' ? (
          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
            <CardHeader className="pb-0">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={prevMonth} data-testid="prev-month-btn" className="rounded-lg"><ChevronLeft size={16} /></Button>
                <CardTitle className="text-[15px] font-outfit font-semibold" data-testid="current-month">{MONTHS[currentMonth]} {currentYear}</CardTitle>
                <Button variant="ghost" size="sm" onClick={nextMonth} data-testid="next-month-btn" className="rounded-lg"><ChevronRight size={16} /></Button>
              </div>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-7 gap-px bg-muted rounded-xl overflow-hidden">
                {DAYS.map(day => (
                  <div key={day} className="bg-muted/50 text-center py-2.5 text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">{day}</div>
                ))}
                {Array.from({ length: firstDay }, (_, i) => (
                  <div key={`empty-${i}`} className="bg-white min-h-[80px]" />
                ))}
                {Array.from({ length: daysInMonth }, (_, i) => {
                  const day = i + 1;
                  const dayLeaves = getLeavesForDay(day);
                  const isToday = day === new Date().getDate() && currentMonth === new Date().getMonth() && currentYear === new Date().getFullYear();
                  return (
                    <div
                      key={day}
                      className={`bg-white min-h-[80px] p-1.5 transition-colors hover:bg-muted/50 ${isToday ? 'ring-2 ring-primary ring-inset' : ''}`}
                      data-testid={`calendar-day-${day}`}
                    >
                      <div className={`text-[11px] font-semibold mb-1 ${isToday ? 'text-primary' : 'text-muted-foreground'}`}>{day}</div>
                      <div className="space-y-0.5">
                        {dayLeaves.slice(0, 3).map((l, idx) => (
                          <div
                            key={idx}
                            className={`text-[9px] px-1 py-0.5 rounded border truncate ${STATUS_COLORS[l.status] || 'bg-muted'}`}
                            title={`${l.employee_name} - ${l.leave_type}`}
                          >
                            {l.employee_name?.split(' ')[0]} - {l.leave_type}
                          </div>
                        ))}
                        {dayLeaves.length > 3 && (
                          <div className="text-[9px] text-muted-foreground font-medium">+{dayLeaves.length - 3} more</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Legend */}
              <div className="flex gap-4 mt-4 pt-3 border-t border-border text-[11px]">
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-success/20 border border-success/30" /> Approved</div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-warning/20 border border-warning/30" /> Pending</div>
                <div className="flex items-center gap-1.5"><div className="w-2.5 h-2.5 rounded bg-error/20 border border-error/30" /> Rejected</div>
              </div>
            </CardContent>
          </Card>
        ) : (
          <Card className="border-0 shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_1px_4px_rgba(0,0,0,0.04)] bg-white rounded-2xl overflow-hidden">
            <CardHeader className="pt-5 px-6 pb-0">
              <CardTitle className="text-[15px] font-outfit font-semibold text-foreground">Employee Leave Balances</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 px-0">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="leave-balances-table">
                  <thead>
                    <tr>
                      <th className="text-left">Employee</th>
                      <th className="text-left">Position</th>
                      <th className="text-center">Annual Entitled</th>
                      <th className="text-center">Annual Used</th>
                      <th className="text-center">Annual Remaining</th>
                      <th className="text-center">Sick Entitled</th>
                      <th className="text-center">Sick Used</th>
                      <th className="text-center">Sick Remaining</th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeBalances.map((emp) => (
                      <tr key={emp.id} data-testid={`balance-row-${emp.id}`}>
                        <td className="font-medium text-foreground">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 rounded-full bg-gradient-to-br from-stone-100 to-stone-200 flex items-center justify-center text-muted-foreground text-[10px] font-bold">
                              {emp.name?.charAt(0)?.toUpperCase()}
                            </div>
                            {emp.name}
                          </div>
                        </td>
                        <td className="text-muted-foreground">{emp.position || '-'}</td>
                        <td className="text-center font-medium">{emp.annual_leave_entitled || 30}</td>
                        <td className="text-center text-warning font-semibold">{emp.annual_used}</td>
                        <td className="text-center">
                          <Badge variant={emp.annual_remaining <= 5 ? 'destructive' : 'default'} className="text-[11px] font-semibold">{emp.annual_remaining}</Badge>
                        </td>
                        <td className="text-center font-medium">{emp.sick_leave_entitled || 15}</td>
                        <td className="text-center text-warning font-semibold">{emp.sick_used}</td>
                        <td className="text-center">
                          <Badge variant={emp.sick_remaining <= 3 ? 'destructive' : 'secondary'} className="text-[11px] font-semibold">{emp.sick_remaining}</Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
}
